'use client';

import { useCallback, useState } from 'react';

import {
  formatTimeSpanForDisplay,
  getRelevantTradingHours,
  isWithinTradingHours,
} from '../lib';
import { InsuranceConditionStatus } from '../api';
import { language } from './language';
import type {
  TRestrictionContent,
  TUseGoldTradingRestrictionParams,
} from './types';

export const useGoldTradingRestriction = ({
  condition,
  action,
}: TUseGoldTradingRestrictionParams) => {
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState<TRestrictionContent>({
    title: '',
    description: '',
    variant: 'warning',
  });

  const close = useCallback(() => setOpen(false), []);

  const guard = useCallback(() => {
    if (!condition) return true;

    const title = action === 'buy' ? language.buyTitle : language.sellTitle;

    const isOpen =
      condition.status === InsuranceConditionStatus.Open ||
      condition.status === InsuranceConditionStatus.OpenWithCondition;

    if (!isOpen) {
      setContent({
        title,
        description: condition.description,
        variant: condition.status === InsuranceConditionStatus.Close ? 'danger' : 'warning',
      });
      setOpen(true);
      return false;
    }

    if (!isWithinTradingHours(condition)) {
      const { openTime, closeTime } = getRelevantTradingHours(condition);
      const formattedOpen = formatTimeSpanForDisplay(openTime);
      const formattedClose = formatTimeSpanForDisplay(closeTime);

      setContent({
        title,
        description:
          action === 'buy'
            ? language.buyTimeLimit(formattedOpen, formattedClose)
            : language.sellTimeLimit(formattedOpen, formattedClose),
        variant: 'warning',
      });
      setOpen(true);
      return false;
    }

    return true;
  }, [action, condition]);

  return {
    guard,
    restrictionModal: {
      open,
      onClose: close,
      title: content.title,
      description: content.description,
      variant: content.variant,
    },
  };
};
